/**
 * V2DashboardPage — the Estimates dashboard (data table default + cards).
 *
 * Faithful to design_reference/js/screens-dashboard.jsx, bound to the real
 * `Project` shape and driven through `useProjectStore` (search + source filter
 * + sort applied by the store; TanStack handles column-sort/display + the
 * Cards toggle). No RCV/total column — `Project` has no such field.
 * PRP-WEB-1 W2.1-2/8: Projects | Estimates tabs; sorted by Updated, sort + columns remembered;
 * delete asks in-app and can be undone for 10 s.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import useProjectStore from '../../stores/projectStore';
import { BackendAPI } from '../../lib/api';
import useImportJobsStore from '../../stores/importJobsStore';
import { readPack, clearPack } from '../../lib/pendingCheckout';
import { useCredits } from '../../hooks/useCredits';
import { useIsPayPerUser } from '../../hooks/useMembership';
import GetCreditsModal from '../components/GetCreditsModal';
import RedeemCodeForm from '../components/RedeemCodeForm';
import ColumnsMenu, { usePersistentVisibility } from '../components/ColumnsMenu';
import { useUndoableProjectDelete } from '../components/ConfirmDeleteDialog';
import { changedAgo, exactTime } from '../components/EstimateCard';
import EstimatesTable, { DashboardTabs, useDashTab } from '../components/EstimatesTable';
import { projectActivityAt } from '../../lib/projectOverviewApi';
import ElapsedTimer from '../components/ElapsedTimer';
import { Banner } from '../components/ui/banner';
import type { Project, ProjectSource } from '../../types';
import { fmtDate } from '../lib/format';
import { LOSS_KIND } from '../lib/semantics';
import { DataTable, TableSkeleton } from '../components/DataTable';
import { SourceChip, StatusPill } from '../components/SourceChip';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Chip } from '../components/ui/chip';
import { Segmented } from '../components/ui/segmented';
import { Pill } from '../components/ui/pill';
import { Avatar } from '../components/ui/avatar';
import { ProjectFieldsFieldset, ProjectFieldValues } from '../components/ProjectFieldsFieldset';
import { Card } from '../components/ui/card';
import { EmptyState } from '../components/ui/empty-state';
import { Skeleton } from '../components/ui/skeleton';
import {
  IconList,
  IconCards,
  IconPlus,
  IconSearch,
  IconArchive,
  IconTrash,
  IconPin,
  IconTag,
  IconBuilding,
} from '../components/icons';

/** The project's source (web/mobile/uploaded). Surfaced top-level by the
 *  backend (metadata is stripped); legacy metadata.source kept as a fallback. */
function projectSource(p: Project): string | null {
  return p.source ?? (p.metadata?.source as string | undefined) ?? null;
}

/** The loss label for a project (loss_type, falling back to damage_type). */
function lossLabel(p: Project): string | null {
  return p.loss_type ?? p.damage_type ?? null;
}

/** A project's last activity in ms (0 when unknown), for the Updated sort. */
function activityMs(p: Project): number {
  return new Date(projectActivityAt(p) ?? '').getTime() || 0;
}

/** Normalizes a possibly-timestamped ISO date down to its YYYY-MM-DD head. */
function dateHead(iso?: string | null): string | null {
  if (!iso) return null;
  return iso.slice(0, 10);
}

const SOURCE_TABS: Array<{ key: ProjectSource | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'web', label: 'Web' },
  { key: 'mobile', label: 'Mobile' },
  { key: 'uploaded', label: 'Uploaded' },
];

/** A minimal "New Project" modal (overlay + Card). */
function NewProjectModal({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const createProject = useProjectStore((s) => s.createProject);
  const [form, setForm] = useState<ProjectFieldValues>({});
  const [saving, setSaving] = useState(false);

  const onChange = (patch: Partial<ProjectFieldValues>) =>
    setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setSaving(true);
    const created = await createProject({
      name: form.name || undefined,
      owner_name: form.owner_name || undefined,
      owner_phone: form.owner_phone || undefined,
      owner_email: form.owner_email || undefined,
      street: form.street || '',
      city: form.city || '',
      state: form.state || '',
      zip: form.zip || '',
      claim_number: form.claim_number || undefined,
      policy_number: form.policy_number || undefined,
      date_of_loss: form.date_of_loss || undefined,
      date_of_inspection: form.date_of_inspection || undefined,
      loss_type: form.loss_type || undefined,
    });
    setSaving(false);
    if (created) {
      navigate(`/project/${created.id}`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center overflow-auto bg-black/55 p-4"
      onClick={onClose}
    >
      <Card
        className="my-8 w-full max-w-[480px] p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-4 text-[15px] font-[600] text-[var(--tx-hi)]">New Project</h2>
        <ProjectFieldsFieldset value={form} onChange={onChange} />
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={saving || (!form.owner_name && !form.name)}
          >
            Create project
          </Button>
        </div>
      </Card>
    </div>
  );
}

/**
 * Stripe-return banner: claims the purchase's credit code directly from the
 * backend (no inbox round-trip) and offers one-click redeem. The code is also
 * emailed as a durable backup.
 */
function PurchaseClaimBanner({
  sessionId,
  onDismiss,
}: {
  sessionId: string;
  onDismiss: () => void;
}) {
  const [claim, setClaim] = useState<{
    status: 'ready' | 'pending';
    code: string | null;
    credits: number | null;
    redeemed: boolean;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    BackendAPI.claimPurchase(sessionId)
      .then((c) => {
        if (!cancelled) setClaim(c);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Could not load your purchase.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <Card className="mb-3.5 p-4" data-testid="purchase-claim">
      {!claim && !error && (
        <p className="text-[13px] text-[var(--tx-mid)]">Confirming your purchase…</p>
      )}
      {error && (
        <p className="text-[13px] text-[var(--tx-mid)]">
          Payment received — your credit code will appear on this dashboard
          under &ldquo;unredeemed codes&rdquo; shortly (refresh if needed). ({error})
        </p>
      )}
      {claim?.status === 'pending' && (
        <p className="text-[13px] text-[var(--tx-mid)]">
          Payment is processing — your credit code will arrive by email shortly.
        </p>
      )}
      {claim?.status === 'ready' && (
        <div>
          <div className="mb-1 text-[13px] font-[580] text-[var(--tx-hi)]">
            Purchase complete — {claim.credits} {claim.credits === 1 ? 'credit' : 'credits'}
          </div>
          {claim.redeemed ? (
            <p className="text-[12.5px] text-[var(--tx-mid)]">
              Your code has been applied to your account. A copy was emailed for your records.
            </p>
          ) : (
            <>
              <p className="mb-2 text-[12.5px] text-[var(--tx-mid)]">
                Your one-time credit code (also emailed to you):{' '}
                <code className="font-[var(--font-mono)] text-[var(--tx-hi)]">{claim.code}</code>
              </p>
              <RedeemCodeForm initialCode={claim.code ?? ''} onRedeemed={onDismiss} />
            </>
          )}
        </div>
      )}
      <div className="mt-2">
        <Button variant="ghost" size="sm" onClick={onDismiss}>
          Dismiss
        </Button>
      </div>
    </Card>
  );
}

/** The V2 Estimates dashboard page. */
export default function V2DashboardPage() {
  const navigate = useNavigate();
  const {
    projects,
    filteredProjects,
    loading,
    searchQuery,
    setSearchQuery,
    sourceFilter,
    setSourceFilter,
    fetchProjects,
    archiveProject,
    deleteProject,
  } = useProjectStore();

  // Background import jobs (B-452) — Processing cards with a live elapsed timer.
  const jobs = useImportJobsStore((s) => s.jobs);
  const removeJob = useImportJobsStore((s) => s.removeJob);
  const removeJobsByProject = useImportJobsStore((s) => s.removeJobsByProject);
  useEffect(() => {
    // Resume polling any non-terminal jobs whenever the dashboard mounts.
    useImportJobsStore.getState().startPolling();
  }, []);

  const [view, setView] = useState<'list' | 'cards'>('list');
  const [showNew, setShowNew] = useState(false);

  // Pay-per-conversion surface: credits, stored imports, Stripe return.
  const payPer = useIsPayPerUser();
  const { balance, refresh: refreshCredits } = useCredits(payPer);
  const [creditsOpen, setCreditsOpen] = useState(false);
  // Unredeemed signup/purchase codes issued to this account — the in-app
  // recovery path (a dismissed banner or lost email never strands a code).
  const [pendingCodes, setPendingCodes] = useState<
    Array<{ code: string; credits: number; source: string }>
  >([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const purchaseState = searchParams.get('purchase');
  const purchaseSession = searchParams.get('session_id');

  const refreshPendingCodes = () => {
    BackendAPI.getPendingCodes()
      .then(setPendingCodes)
      .catch(() => setPendingCodes([]));
  };

  useEffect(() => {
    if (payPer) refreshPendingCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPer]);

  const clearPurchaseParams = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('purchase');
    params.delete('session_id');
    setSearchParams(params, { replace: true });
    void refreshCredits().catch(() => {});
    refreshPendingCodes();
  };

  // Pending-checkout consumer (B-258): a `?pack=` intent stashed on /signup
  // or /login goes STRAIGHT to Stripe checkout after auth — no detour through
  // the buy-credits modal. The stash is cleared BEFORE redirecting so a
  // cancelled checkout can't loop back here.
  const [redirectingToCheckout, setRedirectingToCheckout] = useState(false);
  useEffect(() => {
    if (!payPer) return; // members don't buy packs
    if (purchaseState) {
      clearPack(); // just returned from Stripe — never re-trigger
      return;
    }
    const packId = readPack();
    if (!packId) return;
    clearPack();
    BackendAPI.getBillingPacks()
      .then(({ enabled, packs }) => {
        if (enabled && packs.some((p) => p.id === packId)) {
          setRedirectingToCheckout(true);
          return BackendAPI.createCheckoutSession(packId).then(({ url }) => {
            window.location.assign(url);
          });
        }
        // Unknown/disabled pack: fall back to the normal buy modal.
        setCreditsOpen(true);
        return undefined;
      })
      .catch(() => {
        setRedirectingToCheckout(false);
        setCreditsOpen(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payPer, purchaseState]);

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  // Live source counts over the full (unfiltered) project list.
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: projects.length, web: 0, mobile: 0, uploaded: 0 };
    projects.forEach((p) => {
      const s = projectSource(p);
      if (s && s in c) c[s] += 1;
    });
    return c;
  }, [projects]);

  const handleArchive = (id: string) => {
    void archiveProject(id);
  };
  const [dashTab, selectDashTab] = useDashTab();
  const [visibility, setVisibility] = usePersistentVisibility('aiea-dash-projects-columns');
  // W2.8: the row hides at once; the DELETE goes after the Undo window. B-502: the cascade also
  // removes the project's imports, so its Processing jobs are dropped when the delete is sent.
  const { ask: handleDelete, hiddenIds, elements: deleteUi } = useUndoableProjectDelete((id) => {
    removeJobsByProject(id);
    return deleteProject(id);
  });
  const shownProjects = useMemo(
    () => filteredProjects.filter((p) => !hiddenIds.has(p.id)).sort((a, b) => activityMs(b) - activityMs(a)),
    [filteredProjects, hiddenIds],
  );

  const columns = useMemo<ColumnDef<Project, unknown>[]>(
    () => [
      {
        id: 'insured',
        header: 'Insured',
        accessorFn: (p) => p.owner_name ?? '',
        sortingFn: 'text',
        cell: ({ row }) => (
          <span className="font-[530] text-[var(--tx-hi)]">{row.original.owner_name}</span>
        ),
      },
      {
        id: 'address',
        header: 'Address',
        enableSorting: false,
        cell: ({ row }) => {
          const p = row.original;
          return (
            <div>
              <span className="text-[var(--tx-mid)] group-hover:text-[var(--accent-ink)]">
                {p.street || '—'}
              </span>
              <div className="text-[11px] text-[var(--tx-faint)]">
                {[p.city, p.state].filter(Boolean).join(', ')}
                {p.zip ? ` ${p.zip}` : ''}
              </div>
            </div>
          );
        },
      },
      {
        id: 'claim',
        header: 'Claim #',
        accessorFn: (p) => p.claim_number ?? '',
        cell: ({ row }) =>
          row.original.claim_number ? (
            <span className="font-[var(--font-mono)] text-[11.5px]">
              {row.original.claim_number}
            </span>
          ) : (
            <span className="text-[var(--tx-faint)]">—</span>
          ),
      },
      {
        id: 'policy',
        header: 'Policy #',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.policy_number ? (
            <span className="font-[var(--font-mono)] text-[11.5px]">
              {row.original.policy_number}
            </span>
          ) : (
            <span className="text-[var(--tx-faint)]">—</span>
          ),
      },
      {
        id: 'loss',
        header: 'Loss',
        accessorFn: (p) => lossLabel(p) ?? '',
        cell: ({ row }) => {
          const loss = lossLabel(row.original);
          if (!loss) return <span className="text-[var(--tx-faint)]">—</span>;
          return <Pill kind={LOSS_KIND[loss] ?? 'neutral'}>{loss}</Pill>;
        },
      },
      {
        id: 'dol',
        header: 'Date of loss',
        accessorFn: (p) => p.date_of_loss ?? '',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className="font-[var(--font-mono)] tabular-nums">
            {fmtDate(dateHead(row.original.date_of_loss))}
          </span>
        ),
      },
      {
        id: 'updated',
        header: 'Updated',
        accessorFn: activityMs,
        meta: { align: 'right' },
        cell: ({ row }) => {
          const at = projectActivityAt(row.original);
          return (
            <span className="whitespace-nowrap" title={exactTime(at)} data-testid="project-updated">
              {changedAgo(at).replace(/^changed /, '') || '—'}
            </span>
          );
        },
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (p) => p.status ?? 'active',
        cell: ({ row }) => <StatusPill status={row.original.status} />,
      },
      {
        id: 'source',
        header: 'Source',
        enableSorting: false,
        cell: ({ row }) => <SourceChip source={projectSource(row.original)} />,
      },
      {
        id: 'created',
        header: 'Created',
        accessorFn: (p) => p.created_at ?? '',
        meta: { align: 'right' },
        cell: ({ row }) => (
          <span className="font-[var(--font-mono)] tabular-nums">
            {fmtDate(dateHead(row.original.created_at))}
          </span>
        ),
      },
      {
        id: 'by',
        header: 'By',
        enableSorting: false,
        // "By" = who created the project (creator), not the insured. Falls back
        // to owner_name for legacy rows with no creator.
        cell: ({ row }) => (
          <Avatar name={row.original.created_by ?? row.original.owner_name} size={24} />
        ),
      },
    ],
    [],
  );

  const isEmpty = !loading && projects.length === 0;

  return (
    <div className="p-5">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="m-0 mb-[3px] text-[21px] font-[640] tracking-[-0.03em] text-[var(--tx-hi)]">
            Estimates
          </h1>
          <p className="m-0 text-[12.5px] text-[var(--tx-lo)]">
            {filteredProjects.length} of {projects.length} projects
            {sourceFilter !== 'all' ? ` · ${sourceFilter}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {payPer && (
            <Button variant="secondary" onClick={() => setCreditsOpen(true)}>
              {balance != null ? `${balance} ${balance === 1 ? 'credit' : 'credits'}` : 'Credits'}
              {' · '}Get more
            </Button>
          )}
          <Button variant="primary" onClick={() => navigate('/completeness?new=1')}>
            <IconPlus />
            Import PDF
          </Button>
          <Button variant="secondary" onClick={() => setShowNew(true)}>
            New Project
          </Button>
        </div>
      </div>

      {/* Pending ?pack= intent → straight to Stripe (don't flash the dashboard). */}
      {redirectingToCheckout && (
        <Banner tone="info" className="mb-3.5">
          <span data-testid="checkout-redirect">Taking you to secure checkout…</span>
        </Banner>
      )}

      {/* Stripe return: hand the buyer their credit code right here. */}
      {purchaseState === 'success' && purchaseSession && (
        <PurchaseClaimBanner sessionId={purchaseSession} onDismiss={clearPurchaseParams} />
      )}
      {purchaseState === 'cancelled' && (
        <Banner tone="warn" className="mb-3.5">
          Checkout was cancelled — no charge was made.{' '}
          <button type="button" className="underline" onClick={clearPurchaseParams}>
            Dismiss
          </button>
        </Banner>
      )}

      {/* Unredeemed codes recovery: any signup/purchase code issued to this
          account that hasn't been redeemed yet — one-click apply. */}
      {payPer &&
        pendingCodes.map((pc) => (
          <Banner key={pc.code} tone="ok" className="mb-3.5">
            You have an unredeemed {pc.source === 'signup' ? 'free signup' : 'purchase'}{' '}
            code worth {pc.credits} {pc.credits === 1 ? 'credit' : 'credits'}:{' '}
            <code className="font-[var(--font-mono)]">{pc.code}</code>{' '}
            <button
              type="button"
              className="underline"
              onClick={() => {
                void BackendAPI.redeemCode(pc.code)
                  .then(() => {
                    void refreshCredits().catch(() => {});
                    refreshPendingCodes();
                  })
                  .catch(() => refreshPendingCodes());
              }}
            >
              Redeem now
            </button>
          </Banner>
        ))}

      {/* Processing — in-flight background imports (B-452). Survives navigation
          via importJobsStore; flips to Ready (Open) / Failed (Dismiss). */}
      {jobs.length > 0 && (
        <Card className="mb-3.5 p-4" data-testid="processing-imports">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-[.05em] text-[var(--tx-lo)]">
            Processing
          </div>
          <ul className="divide-y divide-[var(--bd-subtle)]">
            {jobs.map((job) => (
              <li key={job.job_id} className="flex items-center gap-3 py-2">
                <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--tx-hi)]">
                  {job.filename}
                </span>
                {job.status === 'ready' ? (
                  <>
                    <Pill kind="ok">Ready</Pill>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        navigate(
                          `/completeness?import=${encodeURIComponent(job.parse_hash)}` +
                            (job.project_id
                              ? `&project=${encodeURIComponent(job.project_id)}`
                              : ''),
                        );
                        // Drop the terminal job once opened so the Processing
                        // card list doesn't grow unbounded (the import lives on
                        // as a real stored-import row, reachable via the deep link).
                        removeJob(job.job_id);
                      }}
                    >
                      Open
                    </Button>
                  </>
                ) : job.status === 'failed' ? (
                  <>
                    <Pill kind="bad">Failed</Pill>
                    <span className="max-w-[220px] truncate text-[11px] text-[var(--tx-faint)]">
                      {job.error || 'Import failed.'}
                    </span>
                    <Button variant="secondary" size="sm" onClick={() => removeJob(job.job_id)}>
                      Dismiss
                    </Button>
                  </>
                ) : (
                  <>
                    <Pill kind="neutral" dot="var(--accent)">
                      {job.status === 'queued' ? 'Queued' : 'Processing'}
                    </Pill>
                    <ElapsedTimer startedAt={job.started_at} />
                  </>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      <DashboardTabs tab={dashTab} onSelect={selectDashTab} />

      {dashTab === 'estimates' ? (
        <EstimatesTable projects={projects} />
      ) : (
      <>
      {/* Toolbar */}
      <div className="mb-3.5 flex flex-wrap items-center gap-2.5">
        <div className="relative w-[300px]">
          <IconSearch className="pointer-events-none absolute left-[10px] top-1/2 h-[15px] w-[15px] -translate-y-1/2 text-[var(--tx-faint)]" />
          <Input
            placeholder="Search by owner, address, or claim…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-[32px]"
          />
        </div>
        <div className="flex gap-1.5">
          {SOURCE_TABS.map((t) => (
            <Chip
              key={t.key}
              on={sourceFilter === t.key}
              count={counts[t.key]}
              onClick={() => setSourceFilter(t.key)}
            >
              {t.label}
            </Chip>
          ))}
        </div>
        <div className="flex-1" />
        <Segmented<'list' | 'cards'>
          value={view}
          onValueChange={setView}
          options={[
            { value: 'list', label: 'List', icon: <IconList /> },
            { value: 'cards', label: 'Cards', icon: <IconCards /> },
          ]}
        />
        {view === 'list' && (
          <ColumnsMenu
            columns={columns.map((c) => ({ id: c.id as string, label: String(c.header) }))}
            visibility={visibility}
            onChange={setVisibility}
          />
        )}
      </div>

      {/* Body */}
      {loading ? (
        view === 'list' ? (
          <TableSkeleton />
        ) : (
          <CardsSkeleton />
        )
      ) : isEmpty ? (
        <Card>
          <EmptyState
            icon={<IconBuilding />}
            title="No estimates yet"
            description="Create your first project to start building a scope."
            action={
              <Button variant="primary" onClick={() => setShowNew(true)}>
                <IconPlus />
                New Project
              </Button>
            }
          />
        </Card>
      ) : view === 'list' ? (
        <DataTable<Project>
          columns={columns}
          data={shownProjects}
          initialSorting={[{ id: 'updated', desc: true }]}
          sortStorageKey="aiea-dash-projects-sort"
          columnVisibility={visibility}
          onRowClick={(p) => navigate(`/project/${p.id}`)}
          rowActions={(p) => (
            <>
              <Button
                variant="icon"
                size="sm"
                title="Archive"
                onClick={(e) => {
                  e.stopPropagation();
                  handleArchive(p.id);
                }}
              >
                <IconArchive />
              </Button>
              <Button
                variant="icon"
                size="sm"
                title="Delete"
                className="hover:text-[var(--bad)] hover:border-[color-mix(in_oklab,var(--bad)_40%,transparent)]"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(p);
                }}
              >
                <IconTrash />
              </Button>
            </>
          )}
        />
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
          {shownProjects.map((p) => {
            const loss = lossLabel(p);
            return (
              <Card
                key={p.id}
                className="cursor-pointer p-3.5 transition-colors hover:border-[var(--bd-strong)]"
                onClick={() => navigate(`/project/${p.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-[14px] font-[580] text-[var(--tx-hi)]">
                      {p.owner_name}
                    </div>
                    <div className="mt-[3px] flex items-center gap-1 text-[11px] text-[var(--tx-faint)]">
                      <IconPin className="h-3 w-3" />
                      {p.street || '—'}
                    </div>
                    <div className="text-[11px] text-[var(--tx-faint)]">
                      {[p.city, p.state].filter(Boolean).join(', ')}
                      {p.zip ? ` ${p.zip}` : ''}
                    </div>
                  </div>
                  <Avatar name={p.created_by ?? p.owner_name} size={24} />
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {loss && <Pill kind={LOSS_KIND[loss] ?? 'neutral'}>{loss}</Pill>}
                  <StatusPill status={p.status} />
                  {p.claim_number && (
                    <Pill kind="accent">
                      <IconTag className="h-[11px] w-[11px]" />
                      {p.claim_number}
                    </Pill>
                  )}
                </div>
                <div className="my-2.5 border-t border-[var(--bd-subtle)]" />
                <div className="flex items-center justify-between text-[11px] text-[var(--tx-lo)]">
                  <span>Loss {fmtDate(dateHead(p.date_of_loss))}</span>
                  <SourceChip source={projectSource(p)} />
                </div>
              </Card>
            );
          })}
        </div>
      )}
      </>
      )}

      {deleteUi}
      {showNew && <NewProjectModal onClose={() => setShowNew(false)} />}
      {creditsOpen && (
        <GetCreditsModal
          onClose={() => setCreditsOpen(false)}
          onRedeemed={() => {
            void refreshCredits().catch(() => {});
          }}
        />
      )}
    </div>
  );
}

/** Skeleton cards for the cards view. */
function CardsSkeleton() {
  return (
    <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Card key={i} className="p-3.5">
          <Skeleton className="h-4 w-[120px]" />
          <Skeleton className="mt-2 h-3 w-[160px]" />
          <Skeleton className="mt-1.5 h-3 w-[100px]" />
          <div className="mt-3 flex gap-1.5">
            <Skeleton className="h-5 w-[56px]" />
            <Skeleton className="h-5 w-[56px]" />
          </div>
        </Card>
      ))}
    </div>
  );
}

